<template>
    <v-app id="app_approved_reversals">
        <v-container fluid>
            <!-- Header Row -->
            <v-row class="pl-2 pr-6 align-center">
                <v-col cols="12" md="7" class="mt-3">
                    <p class="custom_page_main_title">REJECT PAYMENT REVERSALS</p>
                    <p class="custom_page_main_sub_title">View feedback for rejected reversal requests.</p>
                </v-col>
                <v-col cols="12" md="5" class="px-4 mt-5">
                    <v-row class="custom_tool_bar_style justify-end">
                        <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
                        <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
                            <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
                        </v-btn>
                    </v-row>
                </v-col>
            </v-row>

            <v-card class="custom_card_box mt-8" elevation="1" border="sm opacity-1" rounded="lg">
                <v-card-text class="custom_tool_bar_style">
                    <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF">
                        <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
                    </v-btn>
                    <v-btn class="custom_excel_btn ml-4" variant="outlined" @click="exportExcel">
                        <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;Excel
                    </v-btn>
                </v-card-text>

                <v-divider thickness="2"></v-divider>

                <v-expansion-panels v-model="panelOpen" multiple>
                    <v-expansion-panel value="filters" elevation="0">
                        <v-expansion-panel-title expand-icon="mdi-menu-down">
                            <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
                            <span class="text-body-1 font-weight-bold">FILTERS</span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row class="mt-2" dense>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-date-input v-model="dates" append-inner-icon="mdi-close"
                                        prepend-inner-icon="$calendar" prepend-icon="" label="Select Date Range"
                                        multiple="range" density="comfortable" class="custom_input_field_color"
                                        variant="outlined" @click:append-inner="clearDateRange"></v-date-input>
                                </v-col>

                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectedArea" :items="areaListsWithAll" clearable
                                        item-title="text" item-value="value" label="Area" multiple
                                        class="custom_input_field_color" placeholder="Select area" variant="outlined"
                                        density="comfortable" @update:model-value="handleAreaSelection"></v-autocomplete>
                                </v-col>

                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectedDealer" :items="dealerListsWithAll" clearable
                                        density="comfortable" item-title="text" item-value="value" label="Dealer"
                                        class="custom_input_field_color" multiple placeholder="Select dealer"
                                        variant="outlined" @update:model-value="handleDealerSelection">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="undefined">
                                                <template v-slot:prepend>
                                                    <v-checkbox-btn :model-value="props.selected"
                                                        @click="props.select"></v-checkbox-btn>
                                                </template>
                                                <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                                                <v-list-item-subtitle v-if="item.raw.registration" style="color: #01579B; font-size: 11px;">
                                                    REG NO: {{ item.raw.registration }}
                                                </v-list-item-subtitle>
                                            </v-list-item>
                                        </template>
                                        <template v-slot:selection="{ item }">
                                            <span>{{ item.raw.text }}</span>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                            </v-row>

                            <v-divider class="mb-3 mt-2"></v-divider>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>

                <v-card-text>
                    <!-- Cards Grid Layout -->
                    <v-row>
                        <v-col v-for="(item, index) in paginatedItems" :key="index" cols="12" md="4" class="mb-4">
                            <v-card class="reversal-card" elevation="2" border="sm opacity-1" rounded="lg">
                                <!-- Main Card Content -->
                                <v-card-text class="pa-4 card-content">
                                    <!-- Header with Dealer and Amount -->
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="text-h6 font-weight-bold">{{ item.dealer_name }}</span>
                                        <span class="text-h6 font-weight-bold purple-text">Rs. {{
                                            formatNumber(item.amount) }}</span>
                                    </div>

                                    <!-- Area and Invoice Combined - Fixed vertical alignment -->
                                    <div class="d-flex align-center mb-2">
                                        <span class="text-body-2 text-grey-darken-1 area-invoice-text">
                                            {{ item.area_code }}.<span class="invoice-dot">{{ item.invoice_number
                                                }}</span>
                                        </span>
                                    </div>

                                    <!-- Time and Location -->
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-clock-outline</v-icon>
                                            <span class="text-body-2">{{ item.time }}</span>
                                        </div>
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-map-marker-outline</v-icon>
                                            <span class="text-body-2">{{ item.address }}</span>
                                        </div>
                                    </div>

                                    <!-- Collection Date -->
                                    <div class="d-flex justify-space-between align-center mb-3">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-calendar-outline</v-icon>
                                            <span class="text-body-2">Collection Date</span>
                                        </div>
                                        <span class="text-body-2 font-weight-medium">{{ item.collection_date }}</span>
                                    </div>

                                    <!-- Payment Type Status - Light Green Box with larger icon -->
                                    <v-card class="payment-type-box pa-3 mb-3" variant="flat" elevation="0"
                                        rounded="md">
                                        <div class="d-flex align-center">
                                            <v-icon size="large" :color="getPaymentIconColor(item.payment_type)"
                                                class="mr-3">
                                                {{ getPaymentIcon(item.payment_type) }}
                                            </v-icon>
                                            <div>
                                                <div class="text-body-2 font-weight-bold">{{
                                                    getPaymentTypeText(item.payment_type) }}
                                                </div>
                                                <div class="text-caption text-grey-darken-1">Full payment received</div>
                                            </div>
                                        </div>
                                    </v-card>

                                    <!-- Uploaded Image Section -->
                                    <div class="text-body-1 font-weight-bold mb-2">Uploaded Image</div>

                                    <!-- Image and Details Row - Image height matches 4 lines -->
                                    <div class="d-flex mb-3 image-details-section">
                                        <!-- Image with A4-like shape - fixed height to match 4 lines -->
                                        <div class="image-container mr-3">
                                            <v-img :src="item.image_url" height="96" width="64" cover
                                                class="rounded-sm a4-shape"></v-img>
                                        </div>

                                        <!-- Image Details with dividers - reduced spacing -->
                                        <div class="flex-grow-1">
                                            <div class="d-flex justify-space-between align-center"
                                                style="padding: 4px 0;">
                                                <span class="text-body-2">Uploaded by</span>
                                                <span class="text-body-2 font-weight-medium">{{ item.uploaded_by
                                                    }}</span>
                                            </div>
                                            <v-divider class="my-0" style="margin: 1px 0;"></v-divider>

                                            <div class="d-flex justify-space-between align-center"
                                                style="padding: 4px 0;">
                                                <span class="text-body-2">Uploaded time</span>
                                                <span class="text-body-2">{{ item.uploaded_time }}</span>
                                            </div>
                                            <v-divider class="my-0" style="margin: 1px 0;"></v-divider>

                                            <div class="d-flex justify-space-between align-center"
                                                style="padding: 4px 0;">
                                                <span class="text-body-2">File size</span>
                                                <span class="text-body-2">{{ item.file_size }}</span>
                                            </div>
                                            <v-divider class="my-0" style="margin: 1px 0;"></v-divider>

                                            <div class="d-flex justify-space-between align-center"
                                                style="padding: 4px 0;">
                                                <span class="text-body-2">Format</span>
                                                <span class="text-body-2">{{ item.format }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Reason for Reversal -->
                                    <div class="text-body-1 font-weight-bold mb-2">Reason for Reversal</div>

                                    <!-- Fixed height reason box -->
                                    <v-card class="reason-box pa-3 mb-2 reason-fixed-height" variant="outlined"
                                        elevation="0" border="sm opacity-1" rounded="md">
                                        <span class="text-body-2 reason-text">{{ item.reason }}</span>
                                    </v-card>

                                    <!-- Approval Date - New Section for Approved Page -->
                                    <div class="d-flex justify-space-between align-center mt-2 mb-1">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="green-darken-1"
                                                class="mr-1">mdi-check-circle-outline</v-icon>
                                            <span class="text-body-2 font-weight-medium">Approved on</span>
                                        </div>
                                        <span class="text-body-2">{{ item.approved_date || '2024-01-28' }}</span>
                                    </div>

                                    <!-- Approved By - New Section for Approved Page -->
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="blue-darken-1"
                                                class="mr-1">mdi-account-check-outline</v-icon>
                                            <span class="text-body-2 font-weight-medium">Approved by</span>
                                        </div>
                                        <span class="text-body-2">{{ item.approved_by || 'Admin User' }}</span>
                                    </div>
                                </v-card-text>

                                <!-- View Feedback Button - Changed from Approve Button -->
                                <v-btn class="view-feedback-btn fixed-height-btn" block variant="flat"
                                    @click="viewFeedback(item)">
                                    VIEW FEEDBACK
                                </v-btn>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Pagination with Rows Per Page Selector -->
                    <div class="d-flex justify-end align-center mt-4">
                        <div class="d-flex align-center">
                            <span class="text-body-2 mr-2">Rows per page:</span>
                            <v-select v-model="itemsPerPage" :items="[3, 6, 9, 12]" variant="outlined" density="compact"
                                style="width: 80px;" hide-details class="mr-4"></v-select>
                            <span class="text-body-2 mr-4">
                                {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage,
                                filteredReversals.length) }} of {{ filteredReversals.length }}
                            </span>
                            <v-btn icon="mdi-chevron-left" variant="text" :disabled="currentPage === 1"
                                @click="currentPage--"></v-btn>
                            <v-btn icon="mdi-chevron-right" variant="text" :disabled="currentPage === totalPages"
                                @click="currentPage++"></v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>

            <!-- Feedback View Dialog - Updated Design -->
            <v-dialog v-model="feedbackDialog" max-width="600" persistent>
                <v-card rounded="md">
                    <div class="pa-4">
                        <!-- Header -->
                        <v-card color="#F0F0F0" flat>
                            <template v-slot:prepend>
                                <v-card-title class="text-subtitle-1 font-weight-bold">REVIEW FEEDBACK</v-card-title>
                            </template>
                            <v-spacer></v-spacer>
                            <template v-slot:append>
                                <v-btn @click="feedbackDialog = false" icon="mdi-close" size="x-small"></v-btn>
                            </template>
                        </v-card>

                        <v-card color="white" flat class="mt-3">
                            <v-card-item>
                                <!-- Sales Rep Reason -->
                                <div class="text-body-2 font-weight-medium mb-1" style="color: #424242;">Sales Rep Reason</div>
                                <v-card class="pa-3 mb-4" style="background-color: #FFE5B4 !important;" elevation="0" rounded="md">
                                    <div class="text-body-2 font-weight-medium" style="color: #9C27B0;">Feedback By {{ selectedItem?.uploaded_by || 'Kamal Perera' }}</div>
                                    <div class="text-body-2 mt-1" style="color: #424242; line-height: 1.5;">
                                        {{ selectedItem?.reason || 'I mistakenly entered the incorrect amount on the invoice. After printing it, I noticed the error. Could you please reverse this invoice?' }}
                                    </div>
                                    <div class="text-caption mt-2" style="color: #757575;">{{ selectedItem?.uploaded_time || '09:16 AM' }} | {{ selectedItem?.collection_date || '2024-01-27' }}</div>
                                </v-card>

                                <!-- Area Manager Feedback -->
                                <div class="text-body-2 font-weight-medium mb-1" style="color: #424242;">Area Manager Feedback</div>
                                <v-card class="pa-3 mb-4" style="background-color: #E8F5E9 !important;" elevation="0" rounded="md">
                                    <div class="text-body-2 font-weight-medium" style="color: #2E7D32;">Feedback By {{ selectedItem?.area_manager || 'Saman Fernando' }}</div>
                                    <div class="text-body-2 mt-1" style="color: #424242; line-height: 1.5;">
                                        Please process this request by reversing invoice {{ selectedItem?.invoice_number || 'INV-2024-001' }}. Make sure to update the amount on the invoice and notify the customer right away.
                                    </div>
                                    <div class="text-caption mt-2" style="color: #757575;">{{ selectedItem?.manager_time || '10:45 AM' }} | {{ selectedItem?.manager_date || '2024-01-27' }}</div>
                                </v-card>
                            </v-card-item>
                        </v-card>
                    </div>
                </v-card>
            </v-dialog>
        </v-container>
    </v-app>
</template>

<script>
import axios from 'axios';
import { mapActions } from "vuex";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getPermissions } from "@/plugins/permissions";

export default {
    data() {
        return {
            panelOpen: ['filters'],
            dates: [],
            selectedArea: [],
            selectedDealer: [],
            currentPage: 1,
            itemsPerPage: 6,
            feedbackDialog: false,
            selectedItem: null,

            areaLists: [
                { text: 'Colombo | C117-COIN', value: 'colombo' },
                { text: 'Meegoda | C152-MESJ', value: 'meegoda' },
                { text: 'Rathnapura | C321-RAIN', value: 'rathnapura' },
                { text: 'Kegalle | C751-KEKL', value: 'kegalle' },
            ],

            dealerListsWithDetails: [
                { text: 'Chathumi Furniture', value: 'chathumi', registration: '101227' },
                { text: 'Keson Furniture', value: 'keson', registration: '101228' },
                { text: 'Saviro Furniture', value: 'saviro', registration: '101229' },
                { text: 'Gunawardana Furniture', value: 'gunawardana', registration: '001256' },
                { text: 'Manditha Furniture', value: 'manditha', registration: '001257' },
                { text: 'Lakpahana Furniture', value: 'lakpahana', registration: '001258' },
            ],

            reversals: [
                {
                    id: 1,
                    dealer_name: 'Chathumi Furniture',
                    area_code: 'C117-COIN',
                    invoice_number: 'INV-2024-001',
                    amount: 45000,
                    time: '09:30 AM',
                    address: 'No:24/5, Station Rd Maradana',
                    collection_date: '2024-01-26',
                    payment_type: 'cash',
                    image_url: 'https://via.placeholder.com/64x96',
                    uploaded_by: 'Lahiru Gamage',
                    uploaded_time: '09:38 AM',
                    file_size: '3.4 MB',
                    format: 'JPG',
                    reason: 'I mistakenly entered the incorrect amount on the invoice. After printing it, I noticed the error. Could you please reverse the invoice?',
                    approved_date: '2024-01-27',
                    approved_time: '10:15 AM',
                    approved_by: 'Nuwan Perera',
                    area_manager: 'Saman Fernando',
                    manager_time: '10:45 AM',
                    manager_date: '2024-01-27',
                },
                {
                    id: 2,
                    dealer_name: 'Keson Furniture',
                    area_code: 'C152-MESJ',
                    invoice_number: 'INV-2024-045',
                    amount: 78500,
                    time: '10:15 AM',
                    address: 'No:112, Main Street Meegoda',
                    collection_date: '2024-01-25',
                    payment_type: 'cheque',
                    image_url: 'https://via.placeholder.com/64x96',
                    uploaded_by: 'Nuwan Perera',
                    uploaded_time: '10:22 AM',
                    file_size: '2.1 MB',
                    format: 'PNG',
                    reason: 'The customer returned the goods due to damage during transit. Need to reverse the payment.',
                    approved_date: '2024-01-26',
                    approved_time: '11:30 AM',
                    approved_by: 'Kamal Silva',
                    area_manager: 'Priyantha Weerasinghe',
                    manager_time: '12:00 PM',
                    manager_date: '2024-01-26',
                },
                {
                    id: 3,
                    dealer_name: 'Saviro Furniture',
                    area_code: 'C321-RAIN',
                    invoice_number: 'INV-2024-078',
                    amount: 123000,
                    time: '11:45 AM',
                    address: 'No:78, Lake Road Rathnapura',
                    collection_date: '2024-01-24',
                    payment_type: 'bank',
                    image_url: 'https://via.placeholder.com/64x96',
                    uploaded_by: 'Kamal Silva',
                    uploaded_time: '11:52 AM',
                    file_size: '4.5 MB',
                    format: 'PDF',
                    reason: 'Duplicate payment was made by the customer. Need to reverse one of the transactions.',
                    approved_date: '2024-01-25',
                    approved_time: '09:45 AM',
                    approved_by: 'Malith Fernando',
                    area_manager: 'Chandana Kumara',
                    manager_time: '10:15 AM',
                    manager_date: '2024-01-25',
                },
                {
                    id: 4,
                    dealer_name: 'Gunawardana Furniture',
                    area_code: 'C751-KEKL',
                    invoice_number: 'INV-2024-092',
                    amount: 67200,
                    time: '02:30 PM',
                    address: 'No:34, Temple Road Kegalle',
                    collection_date: '2024-01-23',
                    payment_type: 'cash',
                    image_url: 'https://via.placeholder.com/64x96',
                    uploaded_by: 'Malith Fernando',
                    uploaded_time: '02:38 PM',
                    file_size: '1.8 MB',
                    format: 'JPG',
                    reason: 'Wrong amount entered during cash collection. Should be Rs. 62,700 not Rs. 67,200.',
                    approved_date: '2024-01-24',
                    approved_time: '03:20 PM',
                    approved_by: 'Dinesh Rathnayake',
                    area_manager: 'Nuwan Jayawardena',
                    manager_time: '03:45 PM',
                    manager_date: '2024-01-24',
                },
                {
                    id: 5,
                    dealer_name: 'Manditha Furniture',
                    area_code: 'C117-COIN',
                    invoice_number: 'INV-2024-112',
                    amount: 34500,
                    time: '03:45 PM',
                    address: 'No:56, Hospital Road Colombo',
                    collection_date: '2024-01-22',
                    payment_type: 'cheque',
                    image_url: 'https://via.placeholder.com/64x96',
                    uploaded_by: 'Dinesh Rathnayake',
                    uploaded_time: '03:52 PM',
                    file_size: '3.2 MB',
                    format: 'JPG',
                    reason: 'Cheque was issued for wrong invoice. Need to reverse and re-enter.',
                    approved_date: '2024-01-23',
                    approved_time: '10:00 AM',
                    approved_by: 'Thusitha Jayawardena',
                    area_manager: 'Kapila Gunawardena',
                    manager_time: '10:30 AM',
                    manager_date: '2024-01-23',
                },
                {
                    id: 6,
                    dealer_name: 'Lakpahana Furniture',
                    area_code: 'C152-MESJ',
                    invoice_number: 'INV-2024-156',
                    amount: 89200,
                    time: '09:15 AM',
                    address: 'No:23, Temple Road Meegoda',
                    collection_date: '2024-01-21',
                    payment_type: 'bank',
                    image_url: 'https://via.placeholder.com/64x96',
                    uploaded_by: 'Thusitha Jayawardena',
                    uploaded_time: '09:22 AM',
                    file_size: '5.1 MB',
                    format: 'PDF',
                    reason: 'Bank transfer was made to wrong account. Need to reverse and request correct payment.',
                    approved_date: '2024-01-22',
                    approved_time: '02:15 PM',
                    approved_by: 'Ruwan Perera',
                    area_manager: 'Sunil Rathnayake',
                    manager_time: '02:45 PM',
                    manager_date: '2024-01-22',
                }
            ],

            permissions: {},
        }
    },

    computed: {
        // Computed property for Area lists with "ALL" option
        areaListsWithAll() {
            return [
                { text: 'ALL', value: 'all' },
                ...this.areaLists
            ];
        },

        // Computed property for Dealer lists with "ALL" option
        dealerListsWithAll() {
            return [
                { text: 'ALL', value: 'all', registration: '' },
                ...this.dealerListsWithDetails
            ];
        },

        totalPages() {
            return Math.ceil(this.filteredReversals.length / this.itemsPerPage);
        },

        filteredReversals() {
            return this.reversals.filter(item => {
                let dateMatch = true;
                if (this.dates && this.dates.length > 0) {
                    const startDate = new Date(this.dates[0]);
                    const endDate = new Date(this.dates[this.dates.length - 1]);
                    const itemDate = new Date(item.collection_date);
                    dateMatch = itemDate >= startDate && itemDate <= endDate;
                }

                let areaMatch = true;
                if (this.selectedArea && this.selectedArea.length > 0 && !this.selectedArea.includes('all')) {
                    const itemArea = item.area_code.split(' ')[0].toLowerCase();
                    areaMatch = this.selectedArea.some(area => itemArea === area.toLowerCase());
                }

                let dealerMatch = true;
                if (this.selectedDealer && this.selectedDealer.length > 0 && !this.selectedDealer.includes('all')) {
                    dealerMatch = this.selectedDealer.includes(item.dealer_name.toLowerCase().replace(' ', ''));
                }

                return dateMatch && areaMatch && dealerMatch;
            });
        },

        paginatedItems() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredReversals.slice(start, end);
        }
    },

    watch: {
        itemsPerPage() {
            this.currentPage = 1;
        },
        filteredReversals() {
            this.currentPage = 1;
        }
    },

    methods: {
        ...mapActions(['updateSharedDataCollection']),

        // Handle Area selection with ALL option
        handleAreaSelection(value) {
            if (value.includes('all')) {
                // If "ALL" is selected, select all areas
                this.selectedArea = ['all', ...this.areaLists.map(area => area.value)];
            } else {
                this.selectedArea = value;
            }
        },

        // Handle Dealer selection with ALL option
        handleDealerSelection(value) {
            if (value.includes('all')) {
                // If "ALL" is selected, select all dealers
                this.selectedDealer = ['all', ...this.dealerListsWithDetails.map(dealer => dealer.value)];
            } else {
                this.selectedDealer = value;
            }
        },

        formatNumber(value) {
            return parseFloat(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        },

        clearDateRange() {
            this.dates = [];
        },

        backPageRoute() {
            this.$router.push('/payment-reversals');
        },

        getPaymentIcon(paymentType) {
            switch (paymentType) {
                case 'cash':
                    return 'mdi-cash';
                case 'cheque':
                    return 'mdi-file-document-outline';
                case 'bank':
                    return 'mdi-bank';
                default:
                    return 'mdi-cash';
            }
        },

        getPaymentIconColor(paymentType) {
            switch (paymentType) {
                case 'cash':
                    return '#4CAF50';
                case 'cheque':
                    return '#2196F3';
                case 'bank':
                    return '#9C27B0';
                default:
                    return '#4CAF50';
            }
        },

        getPaymentTypeText(paymentType) {
            switch (paymentType) {
                case 'cash':
                    return 'Cash Payment';
                case 'cheque':
                    return 'Cheque Payment';
                case 'bank':
                    return 'Bank Deposit';
                default:
                    return 'Cash Payment';
            }
        },

        viewFeedback(item) {
            this.selectedItem = item;
            this.feedbackDialog = true;
        },

        exportPDF() {
            const doc = new jsPDF();
            doc.text('Approved Payment Reversals', 14, 10);

            const tableData = this.filteredReversals.map((item, index) => [
                index + 1,
                item.dealer_name,
                item.area_code,
                item.invoice_number,
                `Rs. ${this.formatNumber(item.amount)}`,
                item.collection_date,
                this.getPaymentTypeText(item.payment_type),
                item.approved_by || 'Admin',
                item.approved_date || 'N/A'
            ]);

            const tableColumns = ['#', 'Dealer', 'Area', 'Invoice', 'Amount', 'Date', 'Payment Type', 'Approved By', 'Approved Date'];

            doc.autoTable({
                head: [tableColumns],
                body: tableData,
            });

            doc.save('approved_reversals.pdf');
            this.logDocumentDownload('approved_reversals.pdf', 'pdf');
        },

        exportExcel() {
            const tableData = this.filteredReversals.map((item, index) => ({
                '#': index + 1,
                'Dealer': item.dealer_name,
                'Area': item.area_code,
                'Invoice': item.invoice_number,
                'Amount': `Rs. ${this.formatNumber(item.amount)}`,
                'Collection Date': item.collection_date,
                'Payment Type': this.getPaymentTypeText(item.payment_type),
                'Uploaded By': item.uploaded_by,
                'Reason': item.reason,
                'Approved By': item.approved_by || 'Admin',
                'Approved Date': item.approved_date || 'N/A',
                'Area Manager': item.area_manager || 'N/A'
            }));

            const worksheet = XLSX.utils.json_to_sheet(tableData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Approved Reversals');
            XLSX.writeFile(workbook, 'approved_reversals.xlsx');
            this.logDocumentDownload('approved_reversals.xlsx', 'excel');
        },

        async logDocumentDownload(fileName, fileType) {
            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId');

                await axios.post(`/api/log-document-download`, {
                    user_id: userId,
                    file_type: fileType,
                    file_name: fileName,
                    entity_area: 'Approved Reversals',
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } catch (error) {
                console.error('Error logging download:', error);
            }
        },

        fetchReversalsData() {
            // This is handled by the sample data
        }
    },

    mounted() {
        const dashboardId = 10;
        this.permissions = getPermissions(dashboardId);
        this.fetchReversalsData();
    }
}
</script>

<style lang="scss" scoped>
#app_approved_reversals {
    .custom_font_color_view {
        color: #156CF7 !important;
    }

    .custom_font_color_edit {
        color: #2A8200 !important;
    }

    .custom_font_color_delete {
        color: #BB0000 !important;
    }

    .custom_company_logo_nav {
        width: 110px;
        height: 35px;
    }

    .custom_edit_btn {
        color: #1B5E20 !important;
        font-size: var(--font_size_12) !important;
        font-family: var(--font-family) !important;
        border: 2px #77AF5C solid !important;
        background: #C8E6C9 !important;
        border-radius: 4px !important;
    }

    .custom_delete_btn {
        color: #BB0000 !important;
        font-size: var(--font_size_12) !important;
        font-family: var(--font-family) !important;
        border: 2px #D76C6C solid !important;
        background: #F5D9D9 !important;
        border-radius: 4px !important;
    }

    .custom_input_field_class_view {
        .v-field__input {
            background: #F7F7F7 !important;
            color: #05146F !important;
        }
    }

    .custom_input_field_class_view {
        .v-textarea .v-field__input {
            background: #F7F7F7 !important;
            color: #05146F !important;
        }
    }

    .custom_fullscreen_dialog_toolbar {
        position: fixed !important;
        z-index: 999999 !important;
    }

    .custom_card_box {
        font-family: var(--font-family) !important;
        border-top-left-radius: var(--border_top_left_radius) !important;
        border-top-right-radius: var(--border_top_right_radius) !important;
        border-bottom-right-radius: var(--border_bottom_right_radius) !important;
        box-shadow: 0px 4px 18px 0px #0000001F !important;
    }

    .user_image_view {
        width: 120px;
        height: 120px;
        margin-left: 0px;
        border: 2px solid #dcdcdc;
        border-radius: 50%;
        padding: 4px !important;
        background-color: white;
    }

    /* Custom page title styles */
    .custom_page_main_title {
        font-size: 1.8rem;
        font-weight: 700;
        line-height: 1.2;
        margin-bottom: 0.25rem;
        color: #000000;
    }

    .custom_page_main_sub_title {
        font-size: 0.95rem;
        opacity: 0.6;
        color: #000000;
        margin-top: 0;
    }

    /* Card Styling */
    .reversal-card {
        background: white !important;
        border: 1px solid #E0E0E0 !important;
        overflow: hidden;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;

        &:hover {
            box-shadow: 0 8px 16px rgba(156, 39, 176, 0.1) !important;
        }
    }

    /* Card content - takes remaining space */
    .card-content {
        flex: 1;
    }

    /* Area and Invoice text with centered dot */
    .area-invoice-text {
        display: inline-flex;
        align-items: center;
        line-height: 1.2;

        .invoice-dot {
            display: inline-block;
            vertical-align: middle;
        }
    }

    /* Purple Text */
    .purple-text {
        color: #9C27B0 !important;
    }

    /* Payment Type Box with larger icon */
    .payment-type-box {
        background-color: #E8F5E9 !important;
        border: none !important;

        .v-icon {
            font-size: 30px !important;
        }
    }

    /* A4 Shape Image - fixed height to match 4 lines */
    .a4-shape {
        border: 1px solid #E0E0E0;
        background-color: #FAFAFA;
        width: 80px !important;
        height: 108px !important;
    }

    .image-container {
        display: flex;
        align-items: flex-start;
    }

    /* Image details section - ensures consistent spacing */
    .image-details-section {
        min-height: 96px;
    }

    /* Reason Box - fixed height with scroll if needed */
    .reason-box {
        background-color: #F5F5F5 !important;
        border: 1px solid #E0E0E0 !important;
    }

    .reason-fixed-height {
        height: 100px;
        overflow-y: auto;
    }

    .reason-text {
        line-height: 1.5;
        color: #212121;
        display: block;
    }

    /* View Feedback Button - New style for approved page */
    .view-feedback-btn {
        background: #E1BEE7 !important;
        color: #9C27B0 !important;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        font-size: 0.9rem;
        border-radius: 0 !important;
        border-top: 1px solid #CE93D8 !important;
        margin: 0 !important;
        width: 100%;
    }

    .fixed-height-btn {
        height: 50px !important;
        flex-shrink: 0;

        &:hover {
            background: #CE93D8 !important;
            color: white !important;
        }
    }

    /* Text Styles */
    .text-h6 {
        font-size: 1.1rem !important;
        line-height: 1.4;
    }

    .text-body-1 {
        font-size: 0.95rem !important;
    }

    .text-body-2 {
        font-size: 0.85rem !important;
    }

    .text-caption {
        font-size: 0.75rem !important;
    }

    /* Divider styling */
    .v-divider {
        border-color: rgba(0, 0, 0, 0.12) !important;
        opacity: 0.8;
    }

    /* Pagination styling */
    .v-select {
        .v-field {
            font-size: 0.85rem;
        }
    }

    /* Ensure cards have equal height */
    .v-col {
        display: flex;
    }

    .reversal-card {
        flex: 1;
    }

    /* Dialog Styles */
    .v-dialog {
        .v-card {
            .v-card.color-#F0F0F0 {
                background-color: #F0F0F0;
            }
        }
    }

    /* Responsive */
    @media (max-width: 960px) {
        .v-col-md-4 {
            flex: 0 0 100%;
            max-width: 100%;
        }
    }
}

/* Media Queries */
@media (min-width: 320px) {}
@media (min-width: 480px) {}
@media (min-width: 768px) {}
@media (min-width: 1024px) {}
@media (min-width: 1280px) {}
@media (min-width: 1366px) {}
@media (min-width: 1920px) {}
</style>